import { useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/common";
import { useSettings } from "@/context/SettingsContext";
import { api, formatApiError } from "@/lib/api";

export const BackupPanel = () => {
  const { reload } = useSettings();
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState("");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);

  const download = async () => {
    setBusy("download");
    try {
      const res = await api.get("/backup", { responseType: "blob", timeout: 300000 });
      const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "backup.zip";
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded – keep it somewhere safe (Google Drive, USB…)");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const restore = async () => {
    if (!file || confirm !== "RESTORE") return;
    if (!window.confirm("This REPLACES every booking, invoice, audit entry and setting with the contents of the backup. Continue?")) return;
    setBusy("restore");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/backup/restore?confirm=${confirm}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      setResult(data.restored);
      setConfirm(""); setFile(null);
      reload?.();
      toast.success("Backup restored");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title={<span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Download full backup</span>}>
        <p className="mb-4 text-sm text-slate-600">One ZIP with <b>everything</b>: all bookings, invoices & payments, settings (companies, trucks, rates, lead sources), staff list and the audit history – as JSON for restoring, plus <b>bookings.csv</b> and <b>invoices.csv</b> you can open in Excel. Stored on your own computer, independent of this platform.</p>
        <Button onClick={download} disabled={!!busy} data-testid="backup-download" className="bg-[#0E1E38] text-white hover:bg-[#1a3058]">{busy === "download" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download backup (.zip)</Button>
        <p className="mt-3 text-xs text-slate-500">Tip: do this weekly after payroll. Job photos stay in cloud storage and are linked by URL inside the backup.</p>
      </Panel>
      <Panel title={<span className="inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Restore from backup</span>}>
        <p className="mb-3 text-sm text-slate-600">Replaces all current data with a backup ZIP downloaded from this screen. Use only for recovery or when moving to a new system.</p>
        <div className="space-y-3">
          <Input type="file" accept=".zip" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="backup-file" className="bg-white" />
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} placeholder='Type RESTORE to confirm' data-testid="backup-confirm" className="bg-white font-mono" />
          <Button variant="outline" onClick={restore} disabled={!!busy || !file || confirm !== "RESTORE"} data-testid="backup-restore" className="border-rose-300 text-rose-700 hover:bg-rose-50">{busy === "restore" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Restore backup</Button>
          {result && <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="backup-result">Restored: {Object.entries(result).map(([k, v]) => `${v} ${k.replace("_", " ")}`).join(" · ")}</p>}
        </div>
      </Panel>
    </div>
  );
};
